import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from './LanguageContext';
import { el as elStrings, en as enStrings } from './i18n/app';

const PROVIDERS = [
  {
    id: 'openai',
    label: 'OpenAI',
    logo: '🟢',
    placeholder: 'sk-proj-...',
    billingLink: 'https://platform.openai.com/settings/organization/billing',
    steps: [
      { text: 'Πηγαίνετε στο ', link: 'https://platform.openai.com/api-keys', label: 'platform.openai.com/api-keys' },
      { text: 'Πατήστε "Create new secret key"' },
      { text: 'Αντιγράψτε το key και επικολλήστε το παρακάτω' },
    ],
  },
  {
    id: 'anthropic',
    label: 'Claude (Anthropic)',
    logo: '🟣',
    placeholder: 'sk-ant-...',
    billingLink: 'https://console.anthropic.com/settings/billing',
    steps: [
      { text: 'Πηγαίνετε στο ', link: 'https://console.anthropic.com/settings/keys', label: 'console.anthropic.com' },
      { text: 'Πατήστε "Create Key"' },
      { text: 'Αντιγράψτε το key και επικολλήστε το παρακάτω' },
    ],
  },
] as const;

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Props {
  user: any;
  token: string;
  onComplete: () => void;
}

const STEPS = ['welcome', 'openai', 'done'] as const;
type Step = typeof STEPS[number];

export default function OnboardingWizard({ user, token, onComplete }: Props) {
  const { language } = useLanguage();
  const L = (language === 'en' ? enStrings : elStrings).onboarding;
  const [step, setStep] = useState<Step>('welcome');
  const [apiKey, setApiKey] = useState('');
  const [provider, setProvider] = useState<'openai' | 'anthropic'>('openai');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const finish = async (skipKey = false) => {
    setSaving(true);
    try {
      if (!skipKey && apiKey.trim()) {
        const defaultModel = provider === 'anthropic' ? 'claude-sonnet-4-6' : 'gpt-4o';
        await axios.put(`${API}/organizations/my/settings`, {
          openai_api_key: apiKey.trim(),
          ai_model: defaultModel,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      await axios.put(`${API}/users/me/onboarded`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onComplete();
    } catch {
      setError(L.errorGeneric);
    } finally {
      setSaving(false);
    }
  };

  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  };
  const card: React.CSSProperties = {
    background: '#fff', borderRadius: 16, padding: '40px 48px',
    maxWidth: 560, width: '90%', boxShadow: '0 24px 60px rgba(0,0,0,0.2)',
  };
  const title: React.CSSProperties = { fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 };
  const subtitle: React.CSSProperties = { fontSize: 14, color: '#6b7280', marginBottom: 28, lineHeight: 1.6 };
  const btn: React.CSSProperties = {
    background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8,
    padding: '11px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  };
  const btnSecondary: React.CSSProperties = {
    background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb',
    borderRadius: 8, padding: '11px 20px', fontSize: 14, fontWeight: 500, cursor: 'pointer',
  };
  const stepDots = (
    <div style={{ display: 'flex', gap: 6, marginBottom: 32 }}>
      {STEPS.map((s, i) => (
        <div key={i} style={{
          width: 8, height: 8, borderRadius: '50%',
          background: s === step ? '#4f46e5' : '#e5e7eb',
          transition: 'background 0.3s',
        }} />
      ))}
    </div>
  );

  if (step === 'welcome') return (
    <div style={overlay}>
      <div style={card}>
        {stepDots}
        <div style={{ fontSize: 40, marginBottom: 16 }}>👋</div>
        <div style={title}>{L.welcomeTitle}</div>
        <div style={subtitle}>
          {L.welcomeSubtitle(user.full_name || user.email)}
        </div>
        <button style={btn} onClick={() => setStep('openai')}>{L.welcomeBtn}</button>
      </div>
    </div>
  );

  if (step === 'openai') {
    const activeProvider = PROVIDERS.find(p => p.id === provider)!;
    const stepTexts = provider === 'anthropic' ? L.anthropicSteps : L.openaiSteps;
    return (
      <div style={overlay}>
        <div style={card}>
          {stepDots}
          <div style={{ fontSize: 28, marginBottom: 10 }}>✦</div>
          <div style={title}>{L.aiTitle}</div>
          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 20, lineHeight: 1.6 }}>
            {L.aiSubtitle}<strong>{L.aiSubtitleBold}</strong>{L.aiSubtitleEnd}
          </div>

          {/* Provider tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {PROVIDERS.map(p => (
              <button
                key={p.id}
                onClick={() => { setProvider(p.id as any); setApiKey(''); setError(''); }}
                style={{
                  flex: 1, padding: '9px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', transition: 'all 0.15s',
                  border: provider === p.id ? '2px solid #4f46e5' : '2px solid #e5e7eb',
                  background: provider === p.id ? '#f0f0ff' : '#fff',
                  color: provider === p.id ? '#4f46e5' : '#6b7280',
                }}
              >
                {p.logo} {p.label}
              </button>
            ))}
          </div>

          {/* Steps */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 18px', marginBottom: 18 }}>
            {activeProvider.steps.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: i < activeProvider.steps.length - 1 ? 10 : 0, alignItems: 'flex-start' }}>
                <div style={{
                  width: 20, height: 20, borderRadius: '50%', background: '#4f46e5',
                  color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1,
                }}>{i + 1}</div>
                <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5 }}>
                  {'link' in s
                    ? <>{stepTexts[i]}<a href={s.link} target="_blank" rel="noreferrer" style={{ color: '#4f46e5', fontWeight: 600 }}>{s.label}</a></>
                    : stepTexts[i]}
                </div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
            {L.creditsNote}
            <a href={activeProvider.billingLink} target="_blank" rel="noreferrer" style={{ color: '#4f46e5', fontWeight: 600 }}>
              {L.creditsLink}
            </a>
            {L.creditsNoteEnd}
          </div>

          <input
            type="password"
            placeholder={activeProvider.placeholder}
            value={apiKey}
            onChange={e => { setApiKey(e.target.value); setError(''); }}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 8,
              border: '1px solid #d1d5db', fontSize: 14, marginBottom: 8,
              fontFamily: 'monospace', boxSizing: 'border-box',
            }}
          />
          {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button style={btn} onClick={() => finish(false)} disabled={saving}>
              {saving ? L.saving : apiKey.trim() ? L.saveAndContinue : L.continueBtn}
            </button>
            <button style={btnSecondary} onClick={() => finish(true)} disabled={saving}>
              {L.laterBtn}
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 12, textAlign: 'center' }}>
            {L.addLaterNote}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlay}>
      <div style={card}>
        {stepDots}
        <div style={{ fontSize: 40, marginBottom: 16 }}>🚀</div>
        <div style={title}>{L.doneTitle}</div>
        <div style={subtitle}>{L.doneSubtitle}</div>
        <button style={btn} onClick={onComplete}>{L.doneBtn}</button>
      </div>
    </div>
  );
}
